import { inject, injectable, injectFromBase } from 'inversify'
import { cloneDeep, get, isEmpty, lowerFirst, merge } from 'lodash-es'
import type { Record as VuexOrmRecord } from '@vuex-orm/core'
import type { AxiosRequestConfig, CancelToken } from 'axios'
import type BaseModel from '@/data/models/BaseModel'
import ORMBaseRepository from '@/data/repositories/ormBaseRepository'
import type { HttpService } from '@/services/transport/httpService'
import { SERVICE_TYPES } from '@/core/container/types'
import type { AbstractEndpointsInterface } from '@/services/endpointsService'
import type LoggerService from '@/services/loggerService'
import { httpBuildQuerySerializer } from '@/utils/url'
import type {
  PaginationUrlFilterType,
  PaginationUrlParametersSortType,
  PaginationUrlType,
  PaginationUrlOtherType,
  PaginationUrlFilterSingleType,
  PaginationUrlFilterStrictType,
} from '@/services/tables/types'
import type { Endpoint, EndpointParams } from '@/services/endpoints'
import type {
  ExportResultResponse,
  PdfDownloadLinkResultResponse,
  ExportToPdfResultResponse,
  UpdatedEntityResponse,
  Transportable,
  CreatedEntityData,
  TransportResponse,
} from '@/services/transport/types'
import type { PaginationOffsetResponse, PaginationParams } from '@/services/tables/pagination/types'
import ModelMapper from '@/data/utils/modelMapper'
import { FilterOperationEnum, type ExportBody, type Gridable, type ICancelablePromise } from '@/services/types'
import type SerializerService from '@/services/serializerService'
import { TmRepositoryError } from '@/core/error/tmRepositoryError'
import type { BulkBaseBody, BulkDeleteParams, BulkParams } from '@/services/bulk/types'
import TmLogicError from '@/core/error/tmLogicError'
import type FilterQueryService from '@/services/filterQueryService'
import type { Dict, ModelRaw, NumbersObjectNullable } from '@/types'
import type { ApiBulkQuery, ReadByFilterPayload } from '@/data/repositories/types'
import type { BulkQuery } from '@/services/wrappers/types'
import type { TicketsBulkBasePayload } from '@/data/repositories/domain/tickets/types'

export type DoGrid<R> = (
  endpoint: Endpoint,
  endpointParams: EndpointParams,
  queryParameterBag: PaginationUrlType,
  paginationParamsBag?: PaginationParams,
  searchQuery?: string,
  searchFields?: string[],
  cancelToken?: CancelToken | AbortSignal,
) => Promise<PaginationOffsetResponse<R>>

@injectable()
@injectFromBase()
export default class OrmApiRepository<T extends BaseModel = BaseModel, B extends BulkBaseBody = BulkBaseBody>
  extends ORMBaseRepository<T, Endpoint>
  implements Gridable<T>, Transportable<T, AxiosRequestConfig>
{
  protected transport: HttpService

  constructor(
    @inject(SERVICE_TYPES.SerializerService) protected readonly serializerService: SerializerService,
    @inject(SERVICE_TYPES.Api) protected readonly api: HttpService,
    @inject(SERVICE_TYPES.EndpointsService) protected readonly endpointsService: AbstractEndpointsInterface,
    @inject(SERVICE_TYPES.LoggerService) protected readonly loggerService: LoggerService,
    @inject(SERVICE_TYPES.FilterQueryService) protected readonly filterQueryService: FilterQueryService,
  ) {
    super(loggerService)
    this.transport = api
  }

  public getApiSource() {
    return this.transport
  }

  protected makeCancelableRequest = <TRes>(fn: (cancelToken: CancelToken) => Promise<TRes>) => {
    const cancelToken = this.api.getCancelToken()
    const promise: ICancelablePromise<TRes> = fn(cancelToken.token) as any
    promise.cancel = () => cancelToken.cancel()
    return promise
  }

  public async fillByFilter(filters: PaginationUrlFilterType, isReset = false): Promise<T[]> {
    this.logGroupStart('fillByFilter')
    this.logArgs(JSON.stringify(filters))
    const items = await this.readByFilter(filters)
    if (isReset) {
      await this.create(items)
    } else {
      await this.insertOrUpdate(items)
    }
    this.logGroupEnd()
    return items
  }

  public async readByFilter<R extends VuexOrmRecord = T>(
    filters: PaginationUrlFilterType,
    searchQuery?: string,
    searchFields: string[] = [],
  ): Promise<R[]> {
    const response = await this.fetchByFilter<ReadByFilterPayload<R>>(
      this.getSettingsFetch(),
      filters,
      searchQuery,
      searchFields,
    )
    const items = this.getItemsFromReadByFilterResponseData(response.data)

    return ModelMapper.normalizePayload<R>(items, this.model())
  }

  public async fillAll(isReset = false): Promise<T[]> {
    this.logGroupStart('fillAll')
    const result = this.fillByFilter([], isReset)
    this.logGroupEnd()
    return result
  }

  public async fillByCursor(
    endpoint: Endpoint,
    endpointParams: EndpointParams = [],
    searchQuery = '',
    searchFields: string[] = [],
    page = 1,
    isReset = false,
  ) {
    this.logGroupStart('fillByCursor')
    const { items } = await this.doCursor(endpoint, endpointParams, searchQuery, searchFields, [], page)
    if (isReset) {
      await this.create(items)
    } else {
      await this.insertOrUpdate(items)
    }
    this.logGroupEnd()
    return items
  }

  public getFetchEndpointParams(): EndpointParams {
    return []
  }

  protected getPath(key: Endpoint, id: string) {
    return this.endpointsService.getPath(key, [id])
  }

  protected getUpdatePath(key: Endpoint, data: Record<string, any>): string {
    return this.endpointsService.getPath(key, [data.id])
  }

  protected serializeData(data: T): Record<string, any> {
    return this.serializerService.serialize<T>(this.model(), data)
  }

  public autocompleteRequest(
    search: string,
    searchFields: string[],
    endpointParams: EndpointParams,
    page?: number,
    filters?: PaginationUrlFilterType,
    perPage?: number,
    sort?: PaginationUrlParametersSortType,
    other?: PaginationUrlOtherType,
  ) {
    return this.doAutocomplete(search, searchFields, endpointParams, page, filters, perPage, sort, other)
  }

  protected doAutocomplete(
    search: string,
    searchFields: string[],
    endpointParams: EndpointParams,
    page?: number,
    filters?: PaginationUrlFilterType,
    perPage?: number,
    sort?: PaginationUrlParametersSortType,
    other?: PaginationUrlOtherType,
  ): Promise<PaginationOffsetResponse<T>> {
    // @todo replace with cursor pagination
    return this.doCursor(
      this.getSettingsFetch(),
      endpointParams,
      search,
      searchFields,
      filters,
      page,
      perPage,
      sort,
      other,
    )
  }

  protected fetchByFilter<R>(
    endpoint: Endpoint,
    filters: PaginationUrlFilterType,
    searchQuery?: string,
    searchFields: string[] = [],
  ) {
    return this.getApiSource().get<R>(this.endpointsService.getPath(endpoint), {
      params: {
        perPage: 100,
        ...this.decorateFilterToStrictQueryPart(filters),
        ...(searchQuery && !isEmpty(searchQuery) ? { query: searchQuery, searchFields } : {}),
      },
      paramsSerializer: httpBuildQuerySerializer,
    })
  }

  protected async doCursor(
    endpoint: Endpoint,
    endpointParams: EndpointParams,
    searchQuery: string,
    searchFields: string[],
    filters: PaginationUrlFilterType = [],
    page = 1,
    perPage = 25,
    sort?: PaginationUrlParametersSortType,
    other?: PaginationUrlOtherType,
  ): Promise<PaginationOffsetResponse<T>> {
    return this.doGrid(
      endpoint,
      endpointParams,
      {
        filter: filters,
        sort,
        other,
      },
      {
        // not used
        pageKey: `${page}-${perPage}`,
        requestParams: {
          page,
          perPage,
        },
      },
      searchQuery,
      searchFields,
    )
  }

  protected async doGrid(...args: Parameters<DoGrid<T>>): ReturnType<DoGrid<T>> {
    const response = await this.doRawGridTyped(...args)
    response.items = ModelMapper.normalizePayload(response.items, this.model())
    return response
  }

  protected decorateFilterToStrictQueryPart(queryParts: PaginationUrlFilterType) {
    return this.filterQueryService.decorateFilterToStrictQueryPart(queryParts)
  }

  public createGridRequestParamsWithPreparedFilters(
    queryParameterBag: PaginationUrlType,
    paginationParamsBag: PaginationParams | undefined,
    searchQuery: string | undefined,
    searchFields: string[] | undefined,
    filters: PaginationUrlFilterStrictType,
  ): Record<string, unknown> {
    const requestParams = paginationParamsBag ? paginationParamsBag.requestParams : []
    let params: Record<string, unknown> = { ...requestParams }
    if (Object.keys(queryParameterBag.filter).length > 0) {
      params = { ...params, ...filters }
    }

    if (queryParameterBag.sort && Object.keys(queryParameterBag.sort).length > 0) {
      params = { ...params, sort: { ...queryParameterBag.sort } }
    }

    if (queryParameterBag.groupBy && Object.keys(queryParameterBag.groupBy).length > 0) {
      params = { ...params, groupBy: { ...queryParameterBag.groupBy } }
    }

    if (searchQuery && searchQuery.length > 0) {
      params = { ...params, searchQuery }
    }

    if (searchFields && searchFields.length > 0) {
      params = { ...params, searchFields }
    }

    if (queryParameterBag.other && Object.keys(queryParameterBag.other).length > 0) {
      params = { ...params, ...queryParameterBag.other }
    }

    return params
  }

  public createGridRequestParams(
    queryParameterBag: PaginationUrlType,
    paginationParamsBag?: PaginationParams,
    searchQuery?: string,
    searchFields?: string[],
  ): Record<string, unknown> {
    return this.createGridRequestParamsWithPreparedFilters(
      queryParameterBag,
      paginationParamsBag,
      searchQuery,
      searchFields,
      this.decorateFilterToStrictQueryPart(queryParameterBag.filter),
    )
  }

  protected async doRawGridWithPreparedParams<R>(
    endpoint: Endpoint,
    endpointParams: EndpointParams,
    params: Record<string, unknown>,
    cancelToken?: CancelToken | AbortSignal,
  ) {
    const endpointPath = this.endpointsService.getPath(endpoint, endpointParams)
    const config: AxiosRequestConfig = {
      params,
      paramsSerializer: (formData: Dict) => httpBuildQuerySerializer(formData),
    }

    // Handle both CancelToken (legacy) and AbortSignal (modern)
    if (cancelToken instanceof AbortSignal) {
      config.signal = cancelToken
    } else if (cancelToken) {
      config.cancelToken = cancelToken
    }

    return this.getApiSource().get<R>(endpointPath, config)
  }

  protected async doRawGrid<R>(
    endpoint: Endpoint,
    endpointParams: EndpointParams,
    queryParameterBag: PaginationUrlType,
    paginationParamsBag?: PaginationParams,
    searchQuery?: string,
    searchFields?: string[],
    cancelToken?: CancelToken | AbortSignal,
  ) {
    const params = this.createGridRequestParams(queryParameterBag, paginationParamsBag, searchQuery, searchFields)
    return this.doRawGridWithPreparedParams<R>(endpoint, endpointParams, params, cancelToken)
  }

  protected async doRawGridTyped(
    endpoint: Endpoint,
    endpointParams: EndpointParams,
    queryParameterBag: PaginationUrlType,
    paginationParamsBag?: PaginationParams,
    searchQuery?: string,
    searchFields?: string[],
    cancelToken?: CancelToken | AbortSignal,
  ): Promise<PaginationOffsetResponse<T>> {
    const response = await this.doRawGrid<PaginationOffsetResponse<T>>(
      endpoint,
      endpointParams,
      queryParameterBag,
      paginationParamsBag,
      searchQuery,
      searchFields,
      cancelToken,
    )
    return this.prepareGridResponse(response.data)
  }

  public prepareGridResponse(response: PaginationOffsetResponse<T>): PaginationOffsetResponse<T> {
    response.items = response.items.map((item) => {
      return {
        ...item,
        id: item.id?.toString() || '',
      }
    })
    return response
  }

  public async putForFormRequest<R = UpdatedEntityResponse>(toUpdate: any): Promise<TransportResponse<R>> {
    const { id, ...body } = toUpdate
    const url = this.endpointsService.getPath(this.settings().single!, [id])
    return this.getApiSource().put<R>(url, body)
  }

  public async exportRequest(
    params: ExportBody,
    endpointParams?: EndpointParams,
    customUrl?: string,
  ): Promise<ExportResultResponse> {
    if (!this.settings().exportEndpoint && !customUrl) {
      throw new TmRepositoryError(
        'Please define export endpoint in settings() or provide customUrl to exportRequest method',
      )
    }
    const url = customUrl || this.endpointsService.getPath(this.settings().exportEndpoint!, endpointParams)

    const data: AxiosRequestConfig = {
      params,
      paramsSerializer: (formData: Dict) => httpBuildQuerySerializer(formData),
      responseType: 'blob',
    }

    return this.getApiSource().get<unknown>(url, data) as unknown as ExportResultResponse
  }

  public exportToPdfRequest(id: string): Promise<ExportToPdfResultResponse> {
    const url = this.getExportToPdfRequestUrl(id)
    return this.getApiSource().getFile(url) as Promise<ExportToPdfResultResponse>
  }

  public getPdfDownloadLink(id: string): Promise<PdfDownloadLinkResultResponse> {
    const url = this.getExportToPdfRequestUrl(id)
    return this.getApiSource().get(url) as Promise<PdfDownloadLinkResultResponse>
  }

  public gridRequest(
    queryParameterBag: PaginationUrlType,
    paginationParamsBag?: PaginationParams,
    searchQuery?: string,
    searchFields?: string[],
    cancelToken?: CancelToken | AbortSignal,
    endpoindParams?: EndpointParams,
  ): Promise<PaginationOffsetResponse<T>> {
    return this.doGrid(
      this.getSettingsFetch(),
      endpoindParams ?? this.getFetchEndpointParams(),
      queryParameterBag,
      paginationParamsBag,
      searchQuery,
      searchFields,
      cancelToken,
    )
  }

  public async fetchAllItems() {
    const queryParameterBag: PaginationUrlType = { filter: [] }
    const { items } = await this.gridRequest(queryParameterBag)
    await this.insertOrUpdate(items)
    return items
  }

  public mapBulkDeleteParamsToNewFilters(filters: PaginationUrlFilterType, deleteParams: BulkDeleteParams) {
    const preparedParams: Partial<BulkParams> & { filters?: PaginationUrlFilterType } =
      this.prepareBulkDeleteParams(deleteParams)

    const preparedFilters = cloneDeep(filters)

    let firstFilter: PaginationUrlFilterSingleType
    if (preparedFilters[0]) {
      firstFilter = preparedFilters[0]
    } else {
      firstFilter = {}
      preparedFilters.push(firstFilter)
    }

    if (!preparedParams.all) {
      if (!preparedParams.ids) {
        throw new TmLogicError('preparedParams.ids is undefined')
      }
      firstFilter.id = {
        [FilterOperationEnum.in]: preparedParams.ids.map((t) => +t),
      }
    }

    delete preparedParams.ids
    delete preparedParams.all

    if (preparedFilters.length !== 0) {
      if (preparedParams.filters) {
        throw new TmLogicError('preparedParams.filters should be undefined')
      }
      preparedParams.filters = preparedFilters
    }

    return preparedParams
  }

  public bulkDelete<K = B>(
    _data: K,
    filters: PaginationUrlFilterType,
    deleteParams: BulkDeleteParams,
    endpoindParams?: EndpointParams,
  ) {
    const endpoint = this.settings().bulkDeleteEndpoint
    if (!endpoint) {
      throw new TmLogicError('Specify `bulkDeleteEndpoint`')
    }

    const endpointPath = this.endpointsService.getPath(endpoint, endpoindParams)
    const url = `${endpointPath}?${httpBuildQuerySerializer({ filter: filters })}`
    const preparedParams = this.prepareBulkDeleteParams(deleteParams)

    return this.api.post(url, preparedParams)
  }

  public bulkUpdate<K = B>(updateParameters: K, ids: string[], all: boolean, filters: PaginationUrlFilterType) {
    const endpoint = this.settings().bulkUpdateEndpoint
    if (!endpoint) {
      throw new TmLogicError('Specify `bulkUpdateEndpoint`')
    }
    const params = this.getBulkBasePayload(filters, { ids, all })

    return this.api.post(this.endpointsService.getPath(endpoint), {
      ...params,
      updateParameters,
    })
  }

  public reorder(moveId: string, afterId: string | null) {
    const endpoint = this.settings().reorderEndpoint
    if (!endpoint) {
      throw new TmLogicError('Specify `reorderEndpoint`')
    }

    return this.getApiSource().put<T[]>(this.getPath(endpoint, moveId), {
      afterId,
    })
  }

  public async facetsRequest(endpointParams: EndpointParams = [], types?: string[]) {
    const endpoint = this.settings().facetEndpoint
    if (endpoint) {
      return this.fillFacetsByEndpoint<NumbersObjectNullable>(endpoint, endpointParams, types ? { types } : {})
    }
    throw new TmRepositoryError('Please specify `facetEndpoint`')
  }

  protected getExportToPdfRequestUrl(id: string) {
    if (!this.settings().pdfDownloadEndpoint) {
      throw new TmRepositoryError('Please define pdf download endpoint in settings()')
    }

    return this.endpointsService.getPath(this.settings().pdfDownloadEndpoint!, [id])
  }

  protected async fillFacetsByEndpoint<M>(
    endpoint: Endpoint,
    endpointParams: EndpointParams,
    params?: Record<string, unknown>,
  ): Promise<M> {
    const baseUrl = this.endpointsService.getPath(endpoint, endpointParams)
    const response = await this.getApiSource().get<M>(
      params ? `${baseUrl}?${httpBuildQuerySerializer(params)}` : baseUrl,
    )
    return response.data
  }

  protected prepareBulkDeleteParams(deleteParams: BulkDeleteParams): BulkParams {
    return {
      ...deleteParams,
      all: deleteParams.all ? 1 : 0,
    }
  }

  // @todo: get rid of this after APIv2 excluding
  protected getItemsFromReadByFilterResponseData<R extends VuexOrmRecord = T>(
    responseData: ReadByFilterPayload<R>,
  ): R[] {
    if ('items' in responseData) return responseData.items
    return responseData
  }

  public getFetchPath(params?: EndpointParams) {
    const fetch = this.getSettingsFetch()
    return this.endpointsService.getPath(fetch, params)
  }

  public prepareBulkQuery(bulkQuery: BulkQuery): ApiBulkQuery {
    return {
      ids: bulkQuery.selectedIds,
      all: Number(bulkQuery.isAllSelected),
    }
  }

  protected getSettingsFetch() {
    const { fetch } = this.settings()
    if (!fetch) throw new TmRepositoryError('You must define `fetch` property in settings()')

    return fetch
  }

  /**
   * @deprecated duplicates the method insertItems
   * @see insertItems
   */
  public async addModelToStore(data: Dict<any>): Promise<T> {
    const result = await this.model().insert({ data })
    const key = lowerFirst(this.model().entity)
    return get(result, [key, 0]) as T
  }

  protected getBulkBasePayload(
    paramFilters: PaginationUrlFilterType,
    bulkParams: { ids: string[]; all: boolean },
  ): TicketsBulkBasePayload {
    if (bulkParams.all) {
      return {
        filters: paramFilters,
        searchQuery: '',
      }
    }

    const mergedFilters = merge(paramFilters, [{ id: { in: bulkParams.ids } }])
    return {
      filters: mergedFilters,
      searchQuery: '',
    }
  }

  protected async doGet<M>(key: Endpoint, id: string): Promise<TransportResponse<M>> {
    return this.getApiSource().get<M>(this.getPath(key, id))
  }

  protected async doDelete(key: Endpoint, id: string, config?: AxiosRequestConfig) {
    return this.getApiSource().delete(this.getPath(key, id), config || {})
  }

  protected doPost<ResponseType = CreatedEntityData>(
    key: Endpoint,
    toCreate: Record<string, any>,
    config?: AxiosRequestConfig,
  ) {
    const apiSource = this.getApiSource()
    const path = this.getUpdatePath(key, toCreate)
    return apiSource.post<ResponseType>(path, toCreate, config)
  }

  protected async doPut(key: Endpoint, updated: any) {
    return this.getApiSource().put<UpdatedEntityResponse>(this.getUpdatePath(key, updated), this.serializeData(updated))
  }

  /**
   * Transport
   */
  public override async getRequest(id: string): Promise<ModelRaw<T>> {
    if (!this.hasSingleSetting()) {
      throw new TmRepositoryError(`You must define \`single\` property in settings() for entity ${this.model().entity}`)
    }
    const response = await this.doGet<ModelRaw<T>>(this.settings().single!, id)
    response.data = ModelMapper.normalizePayload(response.data, this.model())
    return response.data
  }

  public putRequest(toUpdate: Record<string, any>): Promise<TransportResponse<UpdatedEntityResponse>> {
    if (!this.hasSingleSetting()) {
      throw new TmRepositoryError(`You must define \`single\` property in settings() for entity ${this.model().entity}`)
    }
    return this.doPut(this.settings().single!, toUpdate)
  }

  public postRequest<ResponseType = CreatedEntityData>(toCreate: Record<string, any>, config?: AxiosRequestConfig) {
    if (!this.settings().create) {
      throw new TmRepositoryError(`You must define \`create\` property in settings() for entity ${this.model().entity}`)
    }
    return this.doPost<ResponseType>(this.settings().create!, toCreate, config)
  }

  public deleteRequest(id: string): Promise<unknown> {
    return this.deleteRequestWithParams(id)
  }

  public deleteRequestWithParams(id: string, config?: Pick<AxiosRequestConfig, 'params' | 'data'>): Promise<unknown> {
    if (!this.hasSingleSetting()) {
      throw new TmRepositoryError(`You must define \`single\` property in settings() for entity ${this.model().entity}`)
    }

    return this.doDelete(this.settings().single!, id, config)
  }
}
